import { useMemo } from 'react';
import { useUploadProgress } from 'src/hooks/useUploadProgress';
import { processNotificationData, getNotificationCounts } from 'src/presentations/components/molecules/notification/notificationUtils';

/**
 * Custom hook for managing notification data fetching and processing
 *
 * NEW ARCHITECTURE:
 * - SSE acts as trigger to refetch API
 * - Uses API data directly (single source of truth)
 * - No more data merging complexity
 * - Filtered data for different sections
 *
 * Flow:
 * 1. SSE event → useUploadProgress refetches API
 * 2. Get fresh data from API
 * 3. Process and filter for notification sections
 */
export function useNotificationData(batchId: string | null, errorProcessingStatuses: string[] = []) {
  // Get data from API (SSE triggers refetch internally)
  const { data: apiData, isLoading, isConnected, refetch } = useUploadProgress(batchId, !!batchId);

  // Processed data with filtering logic and counts - combined for better performance
  const { processedData, counts } = useMemo(() => {
    const data = processNotificationData(apiData || [], errorProcessingStatuses);
    const dataCounts = getNotificationCounts(data.providerLists, data.otherLists, data.underReviewLists, data.processingLists);

    return {
      processedData: data,
      counts: dataCounts,
    };
  }, [apiData, errorProcessingStatuses]);

  return {
    // Processed data
    ...processedData,

    // Raw API data
    rawData: apiData || [],

    // Loading states
    isLoading,
    isSSEConnected: isConnected,

    // Actions
    refetch,

    // Metadata
    hasData: !!apiData && apiData.length > 0,
    totalItems: apiData?.length || 0,

    // Counts
    ...counts,
  };
}
